import { useEffect, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Plus, Menu, X, Search, MessageSquare } from "lucide-react";
import { LogoutButton } from "./LogoutButton";
import { Logo } from "../lib/brand";
import { PostMenu } from "./PostMenu";
import { NotificationBell } from "./NotificationBell";
import { ContextSwitcher } from "./ContextSwitcher";
import { useAuth } from "../lib/auth";
import { useUnreadMessages } from "../lib/useUnreadMessages";
import { HeaderAutocomplete } from "./HeaderAutocomplete";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useI18n } from "../lib/i18n";
import { requestPageLeave } from "../lib/pageLeave";
import { SharedLayoutBg } from "./motion/shared-layout-bg";

const nav = [
  { key: "nav.explore", href: "/marketplace" },
  { key: "nav.howItWorks", href: "/how-it-works" },
  { key: "nav.pricing", href: "/pricing" },
  { key: "nav.blogs", href: "/blogs" },
];

export function Header() {
  const [post, setPost] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [mobileViewport, setMobileViewport] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  const { user, logout } = useAuth();
  const isAdmin = user?.userType === "admin";
  const { t } = useI18n();
  const unreadMsgs = useUnreadMessages();
  const [location, navigate] = useLocation();
  const isHome = location === "/";
  const searchOpen = mobileSearch && (!mobileViewport || isHome);
  const search = useSearch();
  const isHiringView = location === "/dashboard" && new URLSearchParams(search).get("view") === "hiring";
  const isServiceDetail = /^\/s\/[^/]+\/?$/.test(location);
  const isPublicProfileDetail = /^\/u\/[^/]+\/?$/.test(location);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 639px)");
    const update = () => setMobileViewport(media.matches);
    media.addEventListener("change", update);
    update();
    return () => media.removeEventListener("change", update);
  }, []);
  // Leaving Home or resizing to mobile must also close an already-open search.
  useEffect(() => { if ((mobileViewport && !isHome) || isServiceDetail || isPublicProfileDetail || isHiringView) setMobileSearch(false); }, [mobileViewport, isHome, isServiceDetail, isPublicProfileDetail, isHiringView]);
  async function doLogout() { if (!requestPageLeave()) return; await logout(); setMobile(false); navigate("/"); }
  return (
    <>
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-line">
        {/* Wide-screen desktop bar */}
        <div className="hidden xl:flex container-x h-16 items-center gap-4">
          <Logo />
          <nav className="flex items-center gap-0.5 ml-1 flex-1">
            {isAdmin ? (
              <Link href="/admin" className="px-2 py-2 text-[13px] font-semibold text-navy rounded-md hover:bg-navy-50 whitespace-nowrap">{t("nav.adminDashboard")}</Link>
            ) : (
              <SharedLayoutBg className="flex w-auto flex-row items-center gap-0.5" inset={0} pillClassName="rounded-lg bg-navy-50">{nav.map((n) => (
                <Link key={n.key} href={n.href} aria-current={location === n.href ? "page" : undefined} className="px-2 py-2 text-[13px] font-medium text-mut hover:text-navy rounded-md hover:bg-navy-50 whitespace-nowrap">
                  {t(n.key)}
                </Link>
              ))}</SharedLayoutBg>
            )}
          </nav>
          <div className="flex items-center gap-2 ml-auto">
            <HeaderAutocomplete id="desktop-header-search" className="w-56 xl:w-64" inputClassName="field h-9 pl-9 text-sm" />
            {user ? (
              isAdmin ? (
                <NotificationBell />
              ) : (
                <>
                  <ContextSwitcher />
                  <NotificationBell />
                  <Link href="/messages" className="btn-ghost h-9 w-9 p-0 relative" title={t("nav.messages")} aria-label={t("nav.messages")}>
                    <MessageSquare className="w-5 h-5" />
                    {unreadMsgs > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand-red text-white text-[10px] font-bold leading-none grid place-items-center">{unreadMsgs > 9 ? "9+" : unreadMsgs}</span>}
                  </Link>
                </>
              )
            ) : (
              <div className="flex items-center gap-1">
                <Link href="/login" className="btn-ghost">{t("auth.signIn")}</Link>
                <Link href="/signup" className="btn-red">{t("auth.joinFree")}</Link>
              </div>
            )}
            <LanguageSwitcher className="ml-2" />
            {!isAdmin && <button onClick={() => setPost(true)} className="btn-navy ml-2"><Plus className="w-4 h-4" /> {t("action.post")}</button>}
          </div>
        </div>

        {/* Mobile bar — menu · centered logo · search (matches mockup) */}
        <div className="mobile-header-safe-top xl:hidden">
          <div className="container-x h-14 flex items-center">
            <button onClick={() => { setMobileSearch(false); setMobile(true); }} aria-label="Open menu" className="btn-ghost h-10 w-10 p-0 -ml-2"><Menu className="w-6 h-6" strokeWidth={1.8} /></button>
            {searchOpen ? (
              <div className="min-w-0 flex-1 px-2">
                <HeaderAutocomplete id="mobile-header-search" className="w-full" inputClassName="field h-10 w-full pl-9 text-sm" onNavigate={() => setMobileSearch(false)} />
              </div>
            ) : (
              <div className="flex-1 flex justify-center"><Logo /></div>
            )}
            <div className="flex items-center gap-1">
              {user && !searchOpen && <NotificationBell />}
              {searchOpen ? (
                <button onClick={() => setMobileSearch(false)} aria-label="Close search" className="btn-ghost h-10 w-10 p-0 -mr-2"><X className="w-[22px] h-[22px]" strokeWidth={1.8} /></button>
              ) : (
                <button onClick={() => { setMobile(false); setMobileSearch(true); }} aria-label={t("home.search.button")} className={`btn-ghost h-10 w-10 p-0 -mr-2 ${isServiceDetail ? "max-lg:hidden" : !isHome ? "max-sm:hidden" : ""}`}><Search className="w-[22px] h-[22px]" strokeWidth={1.8} /></button>
              )}
            </div>
          </div>
        </div>
      </header>

      {mobile && (
        <div className="mobile-header-safe-top fixed inset-0 z-50 bg-white xl:hidden h-[100dvh] flex flex-col">
          <div className="w-full h-16 shrink-0 flex items-center justify-between border-b border-line px-4 sm:px-6">
            <Logo />
            <button onClick={() => setMobile(false)} aria-label="Close menu" className="btn-ghost h-9 w-9 p-0"><X className="w-5 h-5" /></button>
          </div>
          <div className="w-full max-w-none mx-0 flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] flex flex-col">
            {!isAdmin && nav.map((n) => (
              <Link key={n.key} href={n.href} onClick={() => setMobile(false)} className="px-1 py-4 text-base font-medium text-navy border-b border-line">
                {t(n.key)}
              </Link>
            ))}
            {user ? (
              <div>
                <Link href={isAdmin ? "/admin" : "/dashboard"} onClick={() => setMobile(false)} className="block px-1 py-4 text-base font-medium text-navy border-b border-line">{isAdmin ? t("nav.adminDashboard") : t("nav.dashboard")}</Link>
                {!isAdmin && <Link href="/settings" onClick={() => setMobile(false)} className="block px-1 py-4 text-base font-medium text-navy border-b border-line">{t("nav.accountSettings")}</Link>}
                {!isAdmin && <Link href="/wallet" onClick={() => setMobile(false)} className="block px-1 py-4 text-base font-medium text-navy border-b border-line">{t("nav.walletEscrow")}</Link>}
                {!isAdmin && <Link href="/billing" onClick={() => setMobile(false)} className="block px-1 py-4 text-base font-medium text-navy border-b border-line">{t("nav.myPlan")}</Link>}
                <LogoutButton onLogout={doLogout} variant="outline" className="mt-3 h-12 w-full rounded-lg bg-white text-brand-red">{t("auth.logout")}</LogoutButton>
              </div>
            ) : (
              <div className="flex gap-2 mt-4">
                <Link href="/login" onClick={() => setMobile(false)} className="btn-outline flex-1">{t("auth.signIn")}</Link>
                <Link href="/signup" onClick={() => setMobile(false)} className="btn-red flex-1">{t("auth.joinFree")}</Link>
              </div>
            )}
          </div>
        </div>
      )}
      <PostMenu open={post} onClose={() => setPost(false)} />
    </>
  );
}
